import { type StateBuilder } from '@atlassian/state-machine/builder/types';
import { keep } from '@atlassian/state-machine/common/context';
import { always } from '@atlassian/state-machine/common/guards';

import { asRejectEvent, asyncState } from '../../async-state.ts';
import {
  captureAccessToken,
  captureDocumentDetails,
  captureError,
  captureSessionId,
  type SocketMachineContext,
} from '../../network-state-machine-context.ts';
import { type Events, type EventsLookup } from '../../network-state-machine-events.ts';
import { type State } from '../../network-state-machine.ts';

import { authenticatingAsync } from './authenticating/authenticating-effects.ts';
import { onEnterError } from './error/error-effects.ts';
import {
  syncingFetchSnapshotAsync,
  syncingFetchStepsAsync,
  syncingUpdateChangesAsync,
  syncingApplyChangesAsync,
  syncingApplyChangesStepFailedAsync,
  reconcileOfflineChangesAsync,
} from './syncing/syncing-effects.ts';
import { timeoutAsync } from './timeout.ts';
import { waitingAsync } from './waiting/waiting-effects.ts';
import { onEnterWebSocket } from './websocket/websocket-effects.ts';

export const connectingStates = [
  'paused',
  'starting-socket',
  'grace-period',
  'with-timeout',
  'syncing-fetching-snapshot',
  'syncing-fetching-steps',
  'syncing-applying-changes',
  'syncing-applying-changes-step-failed',
  'syncing-updating-changes',
  'waiting',
  'authenticating',
  'error',
  'reconcile-offline-changes',
] as const;
export type ConnectingStates = (typeof connectingStates)[number];

export const CONNECTION_TIMEOUT_MS = 5 * 60 * 1000;

export const CONNECTION_GRACE_PERIOD_MS = 3 * 1000;

function shouldRetry(ctx: SocketMachineContext) {
  return !ctx.shouldHaltRetry();
}

function shouldNotSkipUpdates(ctx: SocketMachineContext) {
  return !ctx.shouldSkipUpdates();
}

export function connectingState(
  builder: StateBuilder<State, Events, EventsLookup, SocketMachineContext>,
): StateBuilder<State, Events, EventsLookup, SocketMachineContext> {
  return builder
    .state('connecting:paused', (s) =>
      s
        .when('resume', (t) => t.if(always).context(keep).to('connecting:waiting'))
        .when('close', (t) => t.if(always).context(keep).to('connecting:paused')),
    )
    .state(
      'connecting:waiting',
      asyncState(waitingAsync, asRejectEvent, (s) =>
        s.when('debounceComplete', (t) =>
          t.if(always).context(keep).to('connecting:authenticating'),
        ),
      ),
    )
    .state(
      'connecting:with-timeout',
      asyncState(timeoutAsync(CONNECTION_TIMEOUT_MS), asRejectEvent, (s) =>
        s
          .state(
            'connecting:authenticating',
            asyncState(authenticatingAsync, asRejectEvent, (s) =>
              s.when('authenticated', (t) =>
                t
                  .if(always)
                  .context(captureAccessToken)
                  .to('connecting:syncing-fetching-snapshot'),
              ),
            ),
          )
          .state(
            'connecting:syncing-fetching-snapshot',
            asyncState(syncingFetchSnapshotAsync, asRejectEvent, (s) =>
              s
                .when('synced', (t) =>
                  t
                    .if(shouldNotSkipUpdates)
                    .context(captureDocumentDetails)
                    .to('connecting:syncing-fetching-steps'),
                )
                .when('synced', (t) =>
                  t
                    .if(always)
                    .context(captureDocumentDetails)
                    .to('connecting:syncing-applying-changes'),
                ),
            ),
          )
          .state(
            'connecting:syncing-fetching-steps',
            asyncState(syncingFetchStepsAsync, asRejectEvent, (s) =>
              s
                .when('synced', (t) =>
                  t
                    .if(always)
                    .context(captureDocumentDetails)
                    .to('connecting:syncing-applying-changes'),
                )
                .when('rejected', (t) =>
                  t
                    .if(always)
                    .context(captureError)
                    .to('connecting:syncing-applying-changes-step-failed'),
                ),
            ),
          )
          .state(
            'connecting:syncing-applying-changes',
            asyncState(syncingApplyChangesAsync, asRejectEvent, (s) =>
              s.when('synced', (t) =>
                t
                  .if(always)
                  .context(captureDocumentDetails)
                  .to('connecting:reconcile-offline-changes'),
              ),
            ),
          )
          .state(
            'connecting:syncing-applying-changes-step-failed',
            asyncState(syncingApplyChangesStepFailedAsync, asRejectEvent, (s) => s),
          )
          .state(
            'connecting:reconcile-offline-changes',

            asyncState(reconcileOfflineChangesAsync, asRejectEvent, (s) =>
              s.when('reconciled', (t) =>
                t.if(always).context(keep).to('connecting:syncing-updating-changes'),
              ),
            ),
          )
          .state(
            'connecting:syncing-updating-changes',
            asyncState(syncingUpdateChangesAsync, asRejectEvent, (s) =>
              s.when('synced', (t) =>
                t.if(always).context(captureSessionId).to('connecting:starting-socket'),
              ),
            ),
          )
          .state('connecting:starting-socket', (s) =>
            s
              .on('enter', onEnterWebSocket)
              .when('open', (t) =>
                t
                  .if((ctx) => ctx.skipGracePeriod === true)
                  .context((ctx) => {
                    ctx.skipGracePeriod = false;
                    return ctx;
                  })
                  .to('connected'),
              )
              .when('open', (t) =>
                t.if(always).context(keep).to('connecting:grace-period'),
              ),
          )
          .state(
            'connecting:grace-period',
            // We wait for a grace period before we consider the connection to be
            // successful, to account for the relay immediately closing the connection.
            // Without waiting, we end up with a 'connected' event immediately followed
            // by a 'closed' event, which causes effects to be repeated in the UI.
            asyncState(
              timeoutAsync(CONNECTION_GRACE_PERIOD_MS, 'gracePeriodTimeout'),
              asRejectEvent,
              (s) =>
                s.when('gracePeriodTimeout', (t) =>
                  t.if(always).context(keep).to('connected'),
                ),
            ),
          )
          .when('timeout', (t) =>
            t.if(always).context(captureError).to('connecting:error'),
          ),
      ),
    )
    .transientState('connecting:error', (s) =>
      s
        .on('enter', onEnterError)
        .when((t) => t.if(shouldRetry).context(keep).to('closed'))
        .when((t) => t.if(always).context(keep).to('init')),
    )
    .when('pause', (t) => t.if(always).context(keep).to('connecting:paused'))
    .when('close', (t) => t.if(always).context(captureError).to('connecting:error'))
    .when('error', (t) => t.if(always).context(captureError).to('connecting:error'))
    .when('rejected', (t) => t.if(always).context(captureError).to('connecting:error'))
    .when('restart', (t) => t.if(always).context(keep).to('connecting:error'));
}
